import { LAYOUT_CONSTANTS } from "@src/constants";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import type { PollingStepStatus } from "@src/services/polling-status.service";
import type { SchedulePollingProgressSteps } from "@src/services/schedule-appointment.service";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Badge } from "@uhg-abyss/web/ui/Badge";
import { Heading } from "@uhg-abyss/web/ui/Heading";
import { IconSymbol } from "@uhg-abyss/web/ui/IconSymbol";
import { LoadingSpinner } from "@uhg-abyss/web/ui/LoadingSpinner";
import { ModalDialog } from "@uhg-abyss/web/ui/ModalDialog";
import { ProgressBar } from "@uhg-abyss/web/ui/ProgressBar";
import { Text } from "@uhg-abyss/web/ui/Text";

export const SCHEDULING_CRITICAL_STEPS = [
  "Create Patient",
  "Create Insurance",
  "Create Appointment",
] as const;

export type SchedulingCriticalStepName = (typeof SCHEDULING_CRITICAL_STEPS)[number];

export const SCHEDULING_CRITICAL_STEP_DISPLAY_NAMES: Record<SchedulingCriticalStepName, string> = {
  "Create Patient": "Setting up patient chart",
  "Create Insurance": "Adding coverage details to chart",
  "Create Appointment": "Scheduling appointment",
};

export type SchedulingProgressModalProps = {
  isOpen: boolean;
  steps: SchedulePollingProgressSteps;
  percentage: number;
};

const StyledModalDialog = styled(ModalDialog, {
  "abyss-modal-base-header-container": {
    padding: "20px 24px 8px",
  },
  "abyss-modal-base-body": {
    padding: "0 24px 24px",
  },
});

const HeaderContent = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "$core.spacing.200",
  textAlign: "center",
});

const ProgressWrapper = styled("div", {
  margin: "16px 0",
});

const StepsWrapper = styled("div", {
  display: "flex",
  flexDirection: "column",
});

const StepLeftContent = styled("div", {
  display: "inline-flex",
  alignItems: "center",
  gap: "$core.spacing.150",
});

const StatusIcon = styled("span", {
  width: "24px",
  height: "24px",
  minWidth: "24px",
  borderRadius: "999px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  variants: {
    status: {
      complete: {
        backgroundColor: "$app.color.polling.status.icon.complete",
      },
      in_progress: {
        backgroundColor: "$app.color.polling.status.icon.inprogress",
      },
      pending: {
        backgroundColor: "$app.color.polling.status.icon.neutral",
      },
      failed: {
        backgroundColor: "$app.color.polling.status.icon.failed",
      },
      skipped: {
        backgroundColor: "$app.color.polling.status.icon.neutral",
      },
    },
  },
});

const StatusIconDot = styled("span", {
  width: "8px",
  height: "8px",
  borderRadius: "999px",
  backgroundColor: "$app.color.polling.status.icon.inprogress",
  variants: {
    status: {
      in_progress: {
        backgroundColor: "$app.color.white",
      },
      pending: {
        backgroundColor: "$app.color.polling.status.icon.dot.neutral",
      },
      skipped: {
        backgroundColor: "$app.color.polling.status.icon.dot.neutral",
      },
    },
  },
});

const StepRow = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "$core.spacing.200",
  minHeight: "50px",
  padding: "8px 12px",
  borderRadius: "6px",
  variants: {
    variant: {
      inProgress: {
        borderColor: "$app.color.polling.status.steprow.border.inprogress",
        borderStyle: "solid",
        borderWidth: "1px",
        backgroundColor: "$app.color.polling.status.steprow.background.inprogress",
      },
    },
  },
});

const StatusContent = styled("span", {
  display: "inline-flex",
  alignItems: "center",
  gap: "$core.spacing.100",
  paddingTop: "4px",
});

const getStatusLabel = (status: PollingStepStatus): string => {
  if (status === "in_progress") {
    return "IN PROGRESS";
  }
  if (status === "complete") {
    return "COMPLETE";
  }
  if (status === "failed") {
    return "FAILED";
  }
  if (status === "skipped") {
    return "SKIPPED";
  }
  return "PENDING";
};

const getBadgeVariant = (
  status: PollingStepStatus,
): "success" | "warning" | "error" | "neutral" | "info" => {
  if (status === "complete") {
    return "success";
  }
  if (status === "in_progress") {
    return "info";
  }
  if (status === "failed") {
    return "error";
  }
  if (status === "skipped") {
    return "warning";
  }
  return "neutral";
};

export const createInitialSchedulingProgressSteps = (): SchedulePollingProgressSteps => {
  return {
    "Create Patient": "pending",
    "Create Insurance": "pending",
    "Create Appointment": "pending",
  };
};

export const getCompletedSchedulingStepPercentage = (
  steps: SchedulePollingProgressSteps,
): number => {
  const completedCount = SCHEDULING_CRITICAL_STEPS.reduce((count, stepName) => {
    return steps[stepName] === "complete" ? count + 1 : count;
  }, 0);

  return Math.round((completedCount / SCHEDULING_CRITICAL_STEPS.length) * 100);
};

export const SchedulingProgressModal = ({
  isOpen,
  steps,
  percentage,
}: SchedulingProgressModalProps) => {
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);
  const isSmallestMobile = useMediaQuery(MEDIA_QUERIES.smallestMobileMax);

  const renderStatusIcon = (status: PollingStepStatus) => {
    if (status === "complete") {
      return (
        <StatusIcon status="complete">
          <IconSymbol icon="check" color="$app.color.white" size="sm" />
        </StatusIcon>
      );
    }

    if (status === "failed") {
      return (
        <StatusIcon status="failed">
          <IconSymbol icon="close" color="$app.color.white" size="sm" />
        </StatusIcon>
      );
    }

    if (status === "in_progress") {
      return (
        <StatusIcon status="in_progress">
          <StatusIconDot status="in_progress" />
        </StatusIcon>
      );
    }

    if (status === "skipped") {
      return (
        <StatusIcon status="skipped">
          <StatusIconDot status="skipped" />
        </StatusIcon>
      );
    }

    return (
      <StatusIcon status="pending">
        <StatusIconDot status="pending" />
      </StatusIcon>
    );
  };

  return (
    <StyledModalDialog
      isOpen={isOpen}
      closeOnClickOutside={false}
      hideClose
      size={LAYOUT_CONSTANTS.modalDialogWidth}
      data-testid="test-id-scheduling-progress-modal"
      data-id="obc-scheduling-progress-modal"
      data-obc-label="Scheduling Progress Modal"
    >
      <div
        style={{
          padding: isMobile ? "4px" : "24px",
          gap: "16px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <HeaderContent>
          <Heading size="sm">Setting up appointment...</Heading>
        </HeaderContent>

        <ProgressWrapper>
          <ProgressBar
            label=""
            percentage={percentage}
            showProgress
            isLive
            progressLabel={`${percentage}%`}
          />
        </ProgressWrapper>

        <StepsWrapper>
          {SCHEDULING_CRITICAL_STEPS.map((stepName) => {
            const status = steps[stepName];
            const statusLabel = getStatusLabel(status);
            const displayName = SCHEDULING_CRITICAL_STEP_DISPLAY_NAMES[stepName];

            return (
              <StepRow
                key={stepName}
                variant={status === "in_progress" ? "inProgress" : undefined}
                data-testid={`test-id-scheduling-progress-row-${stepName}`}
              >
                <StepLeftContent>
                  {renderStatusIcon(status)}
                  <Text fontSize="sm" fontWeight={status === "in_progress" ? "bold" : "medium"}>
                    {displayName}
                  </Text>
                </StepLeftContent>
                <Badge
                  data-testid={`test-id-scheduling-progress-badge-${stepName}`}
                  variant={getBadgeVariant(status)}
                  css={isSmallestMobile ? { whiteSpace: "unset" } : undefined}
                >
                  <StatusContent
                    data-testid={`test-id-scheduling-progress-status-content-${stepName}`}
                  >
                    {status === "in_progress" ? (
                      <LoadingSpinner
                        size="xs"
                        isLoading={true}
                        ariaLoadingLabel={`${displayName} is in progress`}
                      />
                    ) : null}
                    {statusLabel}
                  </StatusContent>
                </Badge>
              </StepRow>
            );
          })}
        </StepsWrapper>
        <div style={{ textAlign: "center" }}>
          <Text fontSize="xs" color="$app.color.header.text">
            Please wait while details are being synchronized.
          </Text>
        </div>
      </div>
    </StyledModalDialog>
  );
};
